import { useEffect, useState } from 'react';
import { apiSinaisDeBlindagem, type SinaisDoCaso, type SinalDeBlindagem } from '../lib/api';
import '../estilos/imoveis-2026.css';

/**
 * ⭐⭐ **SINAIS DE BLINDAGEM, CAMUFLAGEM OU FRAUDE — 15/09/2026, pedido dele.**
 *
 * ⛔ **São SUGESTÕES do Hub — decisão dele ao aprovar, 15/09/2026:** *"isso
 * precisa ser sugestões/apontamentos da HUB, no qual nós iremos analisar e
 * escrever (escreveremos no tópico correto) se de fato houve/há ou não, a
 * blindagem, fraude e/ou camuflagem."* O painel não escreve em seção e não vai
 * ao documento.
 *
 * *"Mostrar sinais, indícios e/ou fatos de blindagem, camuflagem, fraude e
 * etc."* O Hub cruza o que o caso tem e aponta o FATO, com data e distância da
 * dívida. ⚠️ **Não conclui**: "o Hub apresenta suspeita; nunca classifica"
 * (18/08/2026). A qualificação é de quem assina, nas Hipóteses jurídicas.
 *
 * ⚠️ **Na tela, e só nela, por ora.** O que vai ao relatório é outra decisão.
 */
const GRUPOS: Array<{ grupo: SinalDeBlindagem['grupo']; titulo: string }> = [
  { grupo: 'patrimonio', titulo: 'Patrimônio que saiu ou foi onerado depois da dívida' },
  { grupo: 'empresas', titulo: 'Empresas' },
  { grupo: 'pessoas', titulo: 'Pessoas ligadas às partes' },
];

const emTela = (iso: string) => iso.split('-').reverse().join('/');

export function SinaisDeBlindagem({ casoId, chave }: { casoId: string; chave: string }) {
  const [dados, setDados] = useState<SinaisDoCaso | null | undefined>(undefined);
  const [catalogo, setCatalogo] = useState<Record<string, { nome: string; porQue: string; afasta: string }>>({});

  useEffect(() => {
    let vivo = true;
    apiSinaisDeBlindagem
      .doCaso(casoId)
      .then((r) => {
        if (!vivo) return;
        setDados(r.sinais);
        setCatalogo(r.catalogo ?? {});
      })
      .catch(() => vivo && setDados(null));
    return () => {
      vivo = false;
    };
  }, [casoId, chave]);

  if (dados === undefined) return null;
  if (dados === null) {
    return <p className="ajuda-alerta ajuda-solta">Não consegui montar os sinais deste caso agora.</p>;
  }

  const fortes = dados.sinais.filter((s) => s.peso === 'forte').length;
  const medios = dados.sinais.length - fortes;
  const doGrupo = (g: SinalDeBlindagem['grupo']) => dados.sinais.filter((s) => s.grupo === g).length;

  /*
   * ⭐ **No desenho de Imóveis desde 19/09/2026 (PRD §46.10).** Quadros no alto,
   * uma linha por sinal — o padrão, o que ele toca, o primeiro fato — e a linha
   * abre os outros fatos e o "por que importa / o que pode afastar". ⛔ O que o
   * painel diz não mudou: são apontamentos, não saem no relatório, e a frase
   * que diz isso fica no alto.
   */
  return (
    <section className="imv sinais-blindagem-imv" aria-label="Sinais de blindagem, camuflagem ou fraude">
      <div className="imv-resumo">
        <div className="imv-kpi k-sem">
          <span className="imv-rot">Pedem olhar</span>
          <strong>{fortes}</strong>
          <small>sinais fortes</small>
        </div>
        <div className="imv-kpi k-fora">
          <span className="imv-rot">Contam no conjunto</span>
          <strong>{medios}</strong>
          <small>sozinhos, pesam pouco</small>
        </div>
        <div className="imv-kpi k-onus">
          <span className="imv-rot">Patrimônio</span>
          <strong>{doGrupo('patrimonio')}</strong>
          <small>saiu ou foi onerado depois da dívida</small>
        </div>
        <div className="imv-kpi k-fora">
          <span className="imv-rot">Empresas e pessoas</span>
          <strong>{doGrupo('empresas') + doGrupo('pessoas')}</strong>
          <small>ligadas às partes</small>
        </div>
      </div>

      <div className="imv-legenda">
        <span className="imv-legenda-longa">
          <span className="imv-info">i</span> Apontamentos do Hub para a sua análise — <strong>não saem no
          relatório</strong>. Nenhum prova nada sozinho: se houve ou não blindagem, fraude ou camuflagem, quem
          escreve é a KAIRÓS, no tópico correto.
        </span>
      </div>

      {(dados.referencia || dados.contexto.length > 0) && (
        <div className="imv-fontes">
          <span className="imv-rot">Referência</span>
          {dados.referencia ? (
            <span className="imv-fonte ok">
              <i />
              "Depois" se mede contra {dados.referencia.rotulo} · <span className="dado">{emTela(dados.referencia.data)}</span>
            </span>
          ) : (
            <span className="imv-fonte falta">
              <i />
              Sem marco da dívida no caso
            </span>
          )}
          {dados.contexto.map((c) => (
            <span key={c} className="imv-fonte-texto">
              {c}
            </span>
          ))}
        </div>
      )}

      {dados.sinais.length === 0 && <p className="imv-vazio imv-lista">Nenhum sinal no que o caso tem hoje.</p>}

      {GRUPOS.map(({ grupo, titulo }) => {
        const doGrupoLista = dados.sinais.filter((s) => s.grupo === grupo);
        if (doGrupoLista.length === 0) return null;
        return (
          <div key={grupo} className="imv-lista">
            <div className="imv-cab">
              <span className="imv-rot">
                {titulo} · {doGrupoLista.length}
              </span>
            </div>
            {doGrupoLista.map((s, i) => (
              <LinhaDoSinal key={`${s.sobre}-${i}`} sinal={s} catalogo={catalogo} />
            ))}
          </div>
        );
      })}

      {dados.lacunas.length > 0 && (
        <div className="imv-travas">
          {dados.lacunas.map((l) => (
            <p key={l}>⚠️ {l}</p>
          ))}
        </div>
      )}
    </section>
  );
}

function LinhaDoSinal({
  sinal: s,
  catalogo,
}: {
  sinal: SinalDeBlindagem;
  catalogo: Record<string, { nome: string; porQue: string; afasta: string }>;
}) {
  const [aberto, setAberto] = useState(false);
  const leitura = catalogo[s.padrao];
  return (
    <article className={`imv-linha${aberto ? ' aberta' : ''}`}>
      <button type="button" className="imv-grade imv-grade-sinal imv-linha-topo" onClick={() => setAberto(!aberto)} aria-expanded={aberto}>
        <span className="imv-sit-coluna">
          <span className={`imv-sit ${s.peso === 'forte' ? 'onerado' : 'terceiro'}`}>
            <i />
            {s.peso === 'forte' ? 'Pede olhar' : 'Conta no conjunto'}
          </span>
        </span>
        <span className="imv-bem">
          {leitura && <span className="imv-tag">{leitura.nome}</span>}
          <b>
            {s.sobre}
            {s.parte && <span className="sinal-parte"> · de {s.parte}</span>}
          </b>
          {s.fatos[0] && <small>{s.fatos[0]}</small>}
        </span>
        <span className="imv-chev" aria-hidden="true">
          <svg className="imv-ic" viewBox="0 0 24 24">
            <path d={aberto ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'} />
          </svg>
        </span>
      </button>
      {aberto && (
        <div className="imv-detalhe">
          <div>
            <h5 className="imv-rot">Os fatos</h5>
            <ul className="imv-mini">
              {s.fatos.map((f) => (
                <li key={f}>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          {/* ⭐ O catálogo na tela: por que o fato importa e o que pode afastá-lo. */}
          {leitura && (
            <>
              <div>
                <h5 className="imv-rot">Por que importa</h5>
                <p className="imv-detalhe-texto">{leitura.porQue}</p>
              </div>
              <div>
                <h5 className="imv-rot">O que pode afastar</h5>
                <p className="imv-detalhe-texto">{leitura.afasta}</p>
              </div>
            </>
          )}
        </div>
      )}
    </article>
  );
}
